import { useEffect, useMemo, useRef, useState } from 'react'
import 'src/utils/devSocialBets' // registers window.__injectFakeBets / __clearFakeBets
import {
  useGetAllSocialBets,
  useGetPreviousSocialBets,
  useGetPreviousRaceId,
  useGetTotalBets,
  useGetTotalAmount
} from 'src/store/social'
import { getMultiplierTier } from 'src/components/CrashMultiplierHistory/crashRoundFairness'
import { useGameStore, useGetCurrentLanguage, useGetCurrentGame } from 'src/store/game'
import { useGetIsSocialFocusOpen, useUIActions } from 'src/store/ui'
import { isMarbleGame } from 'src/types/domain/Game'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
import { useClientSideTranslation } from 'src/services/i18n'
import { getLatestEvent } from 'src/utils/raceUtils'
import { getBettingTimeWindow } from 'src/utils/getBettingTimeWindow'
import { getSettledTimeWindow } from 'src/utils/getSettledTimeWindow'
import { getDisplayMultiplier } from 'src/components/CrashChipPanel/useFrameBeacon'
import { crashHeatFor, resolveSocialStateHeat } from 'src/components/CrashChipPanel/crashHeat'
import { resolveCrashEventInfo } from 'src/components/CrashChipPanel/crashEventInfo'
import {
  getRaceCountdownInfo,
  formatCountdownSeconds
} from 'src/components/CrashChipPanel/raceCountdown'
import { useSocialCurrencyOverride } from 'src/components/SocialCurrencyDebugPanel/useSocialCurrencyOverride'
import { getSocialCurrencySymbol } from '../utils/getSocialCurrencySymbol'
import { getSocialUsdRate } from '../utils/getSocialUsdRate'
import {
  SocialBetPanelView,
  type SocialPanelTab,
  type CrashTopBarReadout
} from './SocialBetPanelView'
import { resolveRoundCounts } from './roundCounts'
import { useVisualCrashStatus } from 'src/components/CrashChipPanel/useVisualCrash'
import {
  countWinners,
  formatTopBarValue,
  frozenMeterFor,
  gateCrashPhaseOnVisual,
  resolveBetMeter,
  resolveCrashRoundPhase,
  resolvePillMode,
  sortSocialBets,
  summariseSocialBets,
  sumWonPayouts,
  topStakers,
  type SocialFocus
} from './socialPanelLogic'

// The meter's crowd: how many of the round's biggest stakes wear a disc.
const METER_CROWD_SIZE = 3

// A live multiplier stale by more than this is treated as ABSENT — the hero's
// display loop has parked/unmounted, so fall back to the flat floor rather than
// show a frozen number (mirrors crashChipLogic.barDisplayMultiplier's contract).
const DISPLAY_STALE_MS = 400

export function SocialBetTable() {
  const [activeTab, setActiveTab] = useState<SocialPanelTab>('current')
  const [isCollapsed, setIsCollapsed] = useState(false)
  // Mobile-only two-mode model, v17 — now backed by a SHARED UI-store flag so
  // BOTH this panel AND the page's stream component read the same boolean:
  //   stream mode  (flag false) → the feed collapses to a pill; the full
  //     stream + betting overlay own the top region.
  //   social mode  (flag true)  → the bets list FILLS the top region and the
  //     real <video> shrinks (via CSS, no remount) into the top-right corner
  //     square. Desktop CSS ignores this entirely.
  // The flag STAYS OPEN across round transitions (v17) — only the 'Stream'
  // affordance (onCloseSocial) clears it.
  const isSocialFocusOpen = useGetIsSocialFocusOpen()
  const { socialFocusOpened } = useUIActions()
  const focus: SocialFocus = isSocialFocusOpen ? 'social' : 'stream'

  // Close the shared flag when this panel unmounts (leaving the crash game /
  // interface hidden). The page's stream also reads the flag to corner the
  // <video>; without this reset a stale `true` would keep the stream shrunk
  // after the panel is gone. Round transitions do NOT unmount this panel
  // (showSocial keeps crash mounted through every phase), so this never fires
  // on a round change — the stay-open contract is preserved.
  useEffect(() => {
    return () => socialFocusOpened(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const allBets = useGetAllSocialBets()
  const previousBets = useGetPreviousSocialBets()
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)
  const game = useGetCurrentGame()

  const isCrashGame = isMarbleGame(game) && isCrashGameType(game.track.gameType)
  const marbles = isMarbleGame(game) ? game.marbles : []
  const currency = useGameStore((state) => state.config?.bet?.currency)
  const usdRate = useGameStore((state) => state.config?.usdRate)
  // Staging-only social-feed currency preview (SocialCurrencyDebugPanel);
  // null in production builds — see useSocialCurrencyOverride.
  const override = useSocialCurrencyOverride()
  const effCurrency = override?.currency ?? currency
  // NOTE the deliberate asymmetry: an ACTIVE override REPLACES the server
  // rate even when its usdRate is undefined (no `??` fallback), so the BTC
  // preset demonstrates the excluded behaviour ($ + USD values) instead of
  // borrowing the live rate.
  const effUsdRate = override ? override.usdRate : usdRate
  const { rate: displayMultiplier, converted } = getSocialUsdRate(effCurrency, effUsdRate)
  const currencySymbol = getSocialCurrencySymbol(currentLanguage, effCurrency, converted)

  const betsRecord = activeTab === 'current' ? allBets : previousBets
  const sortedBets = useMemo(() => sortSocialBets(betsRecord), [betsRecord])

  // The server's authoritative per-round counter. Already parsed into the
  // store from every `bet` broadcast — it was simply never read here.
  const serverTotalBets = useGetTotalBets()
  const serverTotalAmount = useGetTotalAmount()

  const currentSummary = useMemo(() => summariseSocialBets(allBets), [allBets])
  const previousSummary = useMemo(() => summariseSocialBets(previousBets), [previousBets])
  const previousWonTotal = useMemo(() => sumWonPayouts(previousBets), [previousBets])
  const currentWonTotal = useMemo(() => sumWonPayouts(allBets), [allBets])
  const winnersCount = useMemo(() => countWinners(allBets), [allBets])

  // ── Crash round state for the top bar / adaptive pill (ASK 3 + ASK 2) ──
  // REUSE the hero's sources — do NOT duplicate the curve or the beacon. The
  // live multiplier is the beacon-preferred DISPLAY clock (read ref-style, no
  // extra rAF); phase/countdown come from the pure race-event helpers.
  const race = isMarbleGame(game) ? game.races[0] : undefined
  const raceEvents = race?.raceEvents ?? []
  const latestEventType = getLatestEvent([...raceEvents])?.eventType
  // v20 — the WHOLE panel rides the visual-gated phase: the event clock leads
  // the video by syncDelta, and flipping 'Crashed'/won-mode/the meter freeze
  // on it announced the round's end seconds early (the tell class every other
  // crash surface gates). While armed-but-unseen the panel stays 'live'.
  const visualCrashStatus = useVisualCrashStatus()
  const { crashKnown, crashedMultiplier } = resolveCrashEventInfo(raceEvents)
  // crashKnown gates the hold: a CANCELLED round has no crash event and must
  // release to 'settled' immediately (review C2 — the visual store stays
  // 'armed' through a cancellation, and holding on it would wedge the panel
  // in 'live' for the whole cancel gap).
  const phase = gateCrashPhaseOnVisual(
    resolveCrashRoundPhase(latestEventType),
    visualCrashStatus,
    crashKnown
  )
  const pillMode = resolvePillMode(phase)
  // v24 (note a): the current tab's uncashed rows flip to LOST once the crash
  // is on the viewer's frames. `phase` is already visual-gated, and crashKnown
  // keeps a CANCELLED round (settled, no crash) from marking refunds as lost.
  const currentRoundCrashed = crashKnown && (phase === 'crashed' || phase === 'settled')

  // v22 — the previous round's crash multiplier (previous-tab chip). The race
  // history in the game store still carries the settled round's events;
  // absent (trimmed/fresh session) the chip hides.
  const previousRaceId = useGetPreviousRaceId()
  const { previousCrashMultiplier, previousRoundCancelled } = useMemo(() => {
    if (!isMarbleGame(game) || !previousRaceId) {
      return { previousCrashMultiplier: null, previousRoundCancelled: false }
    }
    const previousRace = game.races.find((r) => r.uuid === previousRaceId)
    if (!previousRace) {
      return { previousCrashMultiplier: null, previousRoundCancelled: false }
    }
    return {
      previousCrashMultiplier:
        resolveCrashEventInfo(previousRace.raceEvents).crashedMultiplier ?? null,
      // A cancelled round refunded its uncashed bets — the previous tab must
      // display them neutral, never LOST (review C3).
      previousRoundCancelled: previousRace.raceEvents.some(
        (event) => event.eventType === 'race_cancelled'
      )
    }
  }, [game, previousRaceId])

  // ── v23: the bail-out meter — always the CURRENT round's TOTAL WON,
  //    counting up from 0. It clears the instant the next round starts (the
  //    store rotates allBets → empty), so old figures never go stale. The
  //    previous tab is that prior round's settled story. Never total-staked.
  const previousWinners = useMemo(() => countWinners(previousBets), [previousBets])

  // The round's HEADLINE figures come from the server's per-round counter,
  // floored by what this client actually holds. Counting purely from our own
  // rows hid our own failures: a bet lost to a cluster island, the join-time
  // snapshot race or a terminal channel close shrank the headline in step with
  // the list, so a broken feed read as a quiet round. The server figure also
  // self-heals — the next broadcast carries the corrected cumulative total,
  // whereas a client sum can never recover a drop.
  //
  // Current tab only: `totalBets`/`totalAmount` describe the CURRENT round, so
  // the previous tab stays client-derived (that round's settled story).
  const roundCounts = useMemo(
    () =>
      resolveRoundCounts({
        serverTotalBets,
        serverTotalAmount,
        clientCount: currentSummary.count,
        clientStaked: currentSummary.totalStaked,
        winnersCount
      }),
    [
      serverTotalBets,
      serverTotalAmount,
      currentSummary.count,
      currentSummary.totalStaked,
      winnersCount
    ]
  )
  const currentTotals = useMemo(
    () => ({
      count: roundCounts.totalCount,
      winnersCount: roundCounts.winnersCount,
      wonTotal: currentWonTotal
    }),
    [roundCounts, currentWonTotal]
  )
  const previousTotals = useMemo(
    () => ({
      count: previousSummary.count,
      winnersCount: previousWinners,
      wonTotal: previousWonTotal
    }),
    [previousSummary.count, previousWinners, previousWonTotal]
  )
  const meter = useMemo(
    () =>
      activeTab === 'current'
        ? resolveBetMeter({ phase, current: currentTotals })
        : frozenMeterFor(previousTotals),
    [activeTab, phase, currentTotals, previousTotals]
  )
  // The meter crowd is the active tab's biggest stakes; the collapsed pill's
  // crowd is ALWAYS the current round's (v25 — the pill never tells the
  // parked previous tab's story).
  const currentStakers = useMemo(() => topStakers(allBets, METER_CROWD_SIZE), [allBets])
  const previousStakers = useMemo(() => topStakers(previousBets, METER_CROWD_SIZE), [previousBets])
  const stakers = activeTab === 'current' ? currentStakers : previousStakers

  // 1Hz clock for the countdown + a cheap live-multiplier re-read. The hero's
  // rAF owns the real 60fps number; this readout only needs seconds-grade
  // freshness for a compact glance, so no rAF here (perf contract unchanged).
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (!isCrashGame || focus !== 'social') return undefined
    setNowMs(Date.now())
    const id = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [isCrashGame, focus, latestEventType])

  const topBar: CrashTopBarReadout | undefined = useMemo(() => {
    if (!isCrashGame) return undefined
    const reading = getDisplayMultiplier()
    const liveMultiplier = reading && reading.ageMs <= DISPLAY_STALE_MS ? reading.value : null

    let countdownText: string | null = null
    let countdownSeconds: number | null = null
    if (phase === 'betting' || phase === 'settled') {
      const info = getRaceCountdownInfo({
        raceEvents,
        nowMs,
        bettingWindowSec: isMarbleGame(game) ? getBettingTimeWindow(game) : 0,
        settledWindowSec: isMarbleGame(game) ? getSettledTimeWindow(game) : 0
      })
      countdownText = formatCountdownSeconds(info.current)
      countdownSeconds = info.current
    }

    const valueText = formatTopBarValue({
      phase,
      multiplier: liveMultiplier,
      crashMultiplier: crashedMultiplier,
      countdownText
    })

    // v19 — colour the readout like the hero: live climb ramp, red on crash,
    // amber for the final betting seconds. The live rAF below refreshes it
    // per-frame while climbing.
    const stateHeat = resolveSocialStateHeat({
      phase,
      multiplier: liveMultiplier,
      countdownSeconds
    })

    const phaseLabel =
      phase === 'betting'
        ? t('Place your bets')
        : phase === 'live'
          ? t('Live bets')
          : phase === 'crashed'
            ? t('Crashed')
            : t('Next round')

    return { phase, pillMode, phaseLabel, valueText, stateHeat }
    // nowMs drives the per-second refresh; the rest are cheap derivations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCrashGame, phase, pillMode, crashedMultiplier, nowMs, t])

  // ── v18 ASK 2a: HERO-CADENCE live multiplier ────────────────────────────
  // The 1Hz interval above keeps the phase caption + countdown fresh, but the
  // climbing multiplier must track the hero at 60fps, not stair-step once a
  // second. So while live+social we run a small rAF that re-reads the SAME
  // published display clock the hero writes (getDisplayMultiplier — a pure ref
  // read of the singleton; no second beacon/sampler, no curve duplication) and
  // writes the formatted value straight to the state-value span via a ref
  // (imperative, zero re-renders — mirrors the hero's valueRef pattern). The
  // VALUE source is identical to the hero's; only the read cadence changes.
  // Reduced-motion (and any non-live/non-social phase) falls back to the 1Hz
  // React path — the initial paint already shows topBar.valueText.
  const stateValueRef = useRef<HTMLSpanElement | null>(null)
  useEffect(() => {
    if (!isCrashGame || focus !== 'social' || phase !== 'live') return undefined
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return undefined

    let raf = 0
    let lastText = ''
    const tick = () => {
      const el = stateValueRef.current
      if (el) {
        const reading = getDisplayMultiplier()
        const liveMultiplier = reading && reading.ageMs <= DISPLAY_STALE_MS ? reading.value : null
        const text = formatTopBarValue({ phase: 'live', multiplier: liveMultiplier })
        if (text !== lastText) {
          lastText = text
          el.textContent = text
          // Recolour on the same climb ramp as the hero (v19).
          el.dataset.heat = crashHeatFor(liveMultiplier ?? 1)
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [isCrashGame, focus, phase])

  const handleTabClick = (tab: SocialPanelTab) => {
    if (tab === activeTab) {
      // Clicking the already-active tab toggles collapse
      setIsCollapsed((prev) => !prev)
    } else {
      setActiveTab(tab)
      setIsCollapsed(false)
    }
  }

  // v17 STAY-OPEN: a new round (a new race uuid) must NOT close the social
  // view. v16 snapped it back to the stream on every round change; that reset
  // is intentionally gone. The shared flag persists across rounds — the
  // pill/summary and list keep updating live while the view stays open — and
  // ONLY the 'Stream' affordance (onCloseSocial) returns to the stream.

  return (
    <SocialBetPanelView
      activeTab={activeTab}
      converted={converted}
      currency={effCurrency}
      currencySymbol={currencySymbol}
      // Reconciled, NOT the raw client row count. These feed the tab badge, the
      // mobile pill's tally and its progress fill — all of which describe the
      // ROUND, same as the meter band. Leaving them client-derived while the
      // band used the server figure put two different denominators on screen at
      // once ("3 / 12 Bets" in the band, "3 / 9" in the pill).
      currentCount={roundCounts.totalCount}
      currentRoundCrashed={currentRoundCrashed}
      currentStakers={currentStakers}
      currentTotal={roundCounts.stakedTotal}
      currentWonTotal={currentWonTotal}
      displayMultiplier={displayMultiplier}
      focus={focus}
      isCollapsed={isCollapsed}
      isCrashGame={isCrashGame}
      marbles={marbles}
      onCloseSocial={() => socialFocusOpened(false)}
      onOpenSocial={() => socialFocusOpened(true)}
      meter={meter}
      onTabClick={handleTabClick}
      previousCount={previousSummary.count}
      previousCrashMultiplier={previousCrashMultiplier}
      previousCrashTier={
        previousCrashMultiplier != null ? getMultiplierTier(previousCrashMultiplier) : null
      }
      previousRoundCancelled={previousRoundCancelled}
      previousRoundKey={previousRaceId ?? ''}
      previousWonTotal={previousWonTotal}
      sortedBets={sortedBets}
      stakers={stakers}
      stateValueRef={stateValueRef}
      topBar={topBar}
      winnersCount={roundCounts.winnersCount}
    />
  )
}
